import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useStickyEditing — local "I'm actively editing" boolean that goes
 * true on demand (typing, paste, keypress) and stays true for
 * `timeoutMs` of inactivity before flipping back to false.
 *
 * Used by ListPanel + FocusedPanel as a fallback signal for the
 * "editing shared list" band. The band's primary signal is
 * `lock.heldByMe` from useEditLock, but on real mobile (background
 * tabs, spotty network, page-hide lifecycle) Firestore snapshots can
 * lapse for a few seconds, briefly resetting the lock state. Walter
 * reported the band flickering on/off mid-typing as a result.
 *
 * The fix: layer a local-state OR on top of the lock check. As long
 * as the user has typed in the last 10s the band stays visible,
 * regardless of whether the lock snapshot has caught up.
 *
 * Returns: [active, mark]
 *   active:  boolean — true if mark() was called within timeoutMs
 *   mark:    () => void — call from input/keypress handlers
 */
export function useStickyEditing(timeoutMs = 10000) {
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);

  const mark = useCallback(() => {
    setActive(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setActive(false);
      timerRef.current = null;
    }, timeoutMs);
  }, [timeoutMs]);

  // Clean up the pending timer on unmount so a stale callback can't
  // fire setActive on a torn-down component.
  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return [active, mark];
}
